import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, ArrowRight, Check, X } from 'lucide-react';
import { VisitorMappingSections } from './actions/VisitorMappingSections';
import { parseVisitorMappingKey } from './actions/visitorMappingFields';
import SchedulerIntegrationTest from './actions/SchedulerIntegrationTest';

export type SchedulingValueMappings = Record<string, Record<string, string>>;

interface SchedulingFieldsModalProps {
    isOpen: boolean;
    closeModal: () => void;
    formFields: any[];
    onSave: (fieldMappings: Record<string, string>, valueMappings: SchedulingValueMappings) => void;
    existingMappings?: Record<string, string>;
    existingValueMappings?: SchedulingValueMappings;
    integrationName: string;
    integrationId?: string;
}

/** Drop value mappings for fields that are no longer mapped. */
export function pruneValueMappings(
    valueMappings: SchedulingValueMappings,
    fieldMappings: Record<string, string>,
): SchedulingValueMappings {
    const pruned: SchedulingValueMappings = {};
    Object.entries(valueMappings || {}).forEach(([sourceKey, optionMap]) => {
        if (!fieldMappings[sourceKey]) return;
        const entries = Object.entries(optionMap || {}).filter(([, target]) => target.trim());
        if (entries.length === 0) return;
        pruned[sourceKey] = Object.fromEntries(entries);
    });
    return pruned;
}

export default function SchedulingFieldsModal({
    isOpen,
    closeModal,
    formFields,
    onSave,
    existingMappings = {},
    existingValueMappings = {},
    integrationName,
    integrationId,
}: SchedulingFieldsModalProps) {
    const [fieldMappings, setFieldMappings] = useState<Record<string, string>>(existingMappings);
    const [valueMappings, setValueMappings] = useState<SchedulingValueMappings>(existingValueMappings);
    const [selectedField, setSelectedField] = useState<string | null>(null);
    const [isTesting, setIsTesting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setFieldMappings(existingMappings);
            setValueMappings(existingValueMappings);
            setSelectedField(null);
            setIsTesting(false);
        }
        // Reset drafts only when the modal opens: the parent passes `|| {}`
        // fallbacks whose identity changes every render, so depending on them
        // would wipe in-progress edits while the modal is open.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const findOptionField = (mappingKey: string) => {
        const parsed = parseVisitorMappingKey(mappingKey);
        if (parsed.source !== 'form' && parsed.source !== 'legacy') return null;
        const field = (formFields || []).find((candidate: any) => candidate?.name === parsed.name);
        if (!field || (field.type !== 'dropdown' && field.type !== 'multiselect')) return null;
        if (!Array.isArray(field.options) || field.options.length === 0) return null;
        return field;
    };

    const handleFieldSelect = (fieldName: string) => {
        setSelectedField(fieldName);
    };

    const handleMappingUpdate = (fieldName: string, value: string) => {
        const trimmed = value.trim();
        if (!trimmed) {
            handleClearMapping(fieldName);
            return;
        }
        setFieldMappings(prev => ({
            ...prev,
            [fieldName]: trimmed
        }));
        // Keep option-backed fields expanded so the Option Values inputs are
        // visible right after mapping; scalar fields collapse as before.
        if (!findOptionField(fieldName)) {
            setSelectedField(null);
        }
    };

    const handleClearMapping = (fieldName: string) => {
        const newMappings = { ...fieldMappings };
        delete newMappings[fieldName];
        setFieldMappings(newMappings);
        setValueMappings(prev => {
            if (!prev[fieldName]) return prev;
            const next = { ...prev };
            delete next[fieldName];
            return next;
        });
    };

    const handleValueMappingChange = (sourceKey: string, next: Record<string, string>) => {
        setValueMappings(prev => ({
            ...prev,
            [sourceKey]: next,
        }));
    };

    const handleCancel = () => {
        setFieldMappings(existingMappings);
        setValueMappings(existingValueMappings);
        setSelectedField(null);
        setIsTesting(false);
        closeModal();
    };

    const handleSaveAll = () => {
        if (Object.keys(fieldMappings).length === 0) {
            return;
        }
        onSave(fieldMappings, pruneValueMappings(valueMappings, fieldMappings));
        closeModal();
    };

    const isEditing = Object.keys(existingMappings).length > 0;

    return (
        <Dialog open={isOpen} onOpenChange={closeModal}>
            <DialogContent className={`${isTesting ? 'sm:max-w-5xl' : 'sm:max-w-2xl'} max-h-[90vh] flex flex-col overflow-hidden`}>
                <DialogHeader>
                    <DialogTitle>{isTesting ? `Test ${integrationName}` : 'Configure Action'}</DialogTitle>
                    <DialogDescription>
                        {isTesting
                            ? 'Submit sample values using the current draft mappings and preview the live scheduler.'
                            : `Map visitor data to ${integrationName} field IDs.`}
                    </DialogDescription>
                </DialogHeader>

                {isTesting && integrationId ? (
                    <SchedulerIntegrationTest
                        integrationId={integrationId}
                        integrationName={integrationName}
                        fieldMappings={fieldMappings}
                        valueMappings={pruneValueMappings(valueMappings, fieldMappings)}
                        formFields={formFields}
                        onBack={() => setIsTesting(false)}
                    />
                ) : (
                    <>
                        <div className="mt-6 flex-1 min-h-0 flex flex-col gap-4">
                            <div className="min-h-0 overflow-y-auto pr-1">
                                <VisitorMappingSections
                                    formFields={formFields}
                                    mappings={fieldMappings}
                                    selectedKey={selectedField}
                                    onSelectKey={handleFieldSelect}
                                    onMapKey={handleMappingUpdate}
                                    onClearKey={handleClearMapping}
                                    getTargetLabel={(value) => value}
                                    targetLabel={`Enter ${integrationName} field ID`}
                                    queryTargetLabel={`${integrationName} field ID`}
                                    renderTargetInput={({ mappingKey, value, onSelect }) => {
                                        const optionField = findOptionField(mappingKey);
                                        return (
                                            <div className="space-y-3">
                                                <SchedulingTargetInput
                                                    value={value || ''}
                                                    onSave={onSelect}
                                                />
                                                {optionField && value && (
                                                    <SchedulingValueMapping
                                                        integrationName={integrationName}
                                                        targetKey={value}
                                                        fieldOptions={optionField.options}
                                                        mapping={valueMappings[mappingKey] || {}}
                                                        onChange={(next) => handleValueMappingChange(mappingKey, next)}
                                                    />
                                                )}
                                            </div>
                                        );
                                    }}
                                />
                            </div>
                        </div>

                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => setIsTesting(true)}
                                disabled={!integrationId || Object.keys(fieldMappings).length === 0}
                            >
                                Test Integration
                            </Button>
                            <Button
                                onClick={handleSaveAll}
                                disabled={Object.keys(fieldMappings).length === 0}
                            >
                                {isEditing ? 'Save Action' : 'Create Action'}
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

function SchedulingValueMapping({
    integrationName,
    targetKey,
    fieldOptions,
    mapping,
    onChange,
}: {
    integrationName: string;
    targetKey: string;
    fieldOptions: string[];
    mapping: Record<string, string>;
    onChange: (next: Record<string, string>) => void;
}) {
    const staleOptions = Object.keys(mapping || {}).filter(opt => !fieldOptions.includes(opt));

    const handleOptionChange = (option: string, target: string) => {
        const next = { ...mapping };
        if (!target) {
            delete next[option];
        } else {
            next[option] = target;
        }
        onChange(next);
    };

    const removeStale = (option: string) => {
        const next = { ...mapping };
        delete next[option];
        onChange(next);
    };

    return (
        <div className="rounded-lg border bg-background p-3 space-y-2">
            <div>
                <div className="text-xs font-medium">Option Values</div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                    Value sent to <span className="font-mono">{targetKey}</span> for each option — use this
                    when {integrationName} expects different values than your option text (for example
                    numeric codes). Leave blank to send the option text as-is.
                </p>
            </div>
            <div className="space-y-1.5">
                {fieldOptions.map((option) => (
                    <div key={option} className="flex items-center gap-2">
                        <span className="flex-1 min-w-0 text-xs truncate" title={option}>{option}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        <Input
                            value={mapping?.[option] || ''}
                            onChange={(event) => handleOptionChange(option, event.target.value)}
                            placeholder="Same as option text"
                            className="h-8 flex-1 text-xs"
                        />
                    </div>
                ))}
            </div>
            {staleOptions.length > 0 && (
                <div className="pt-1 space-y-1.5">
                    <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                        <AlertCircle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-px" />
                        <span>
                            These mapped options no longer exist on the field (renamed or removed):
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {staleOptions.map((option) => (
                            <Badge key={option} variant="secondary" className="gap-1 text-[11px]">
                                <span className="truncate max-w-[160px]">{option}</span>
                                <button
                                    type="button"
                                    className="ml-0.5 rounded hover:bg-muted px-0.5"
                                    onClick={() => removeStale(option)}
                                    aria-label={`Remove stale value mapping for ${option}`}
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </Badge>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function SchedulingTargetInput({ value, onSave }: { value: string; onSave: (value: string) => void }) {
    const [draftValue, setDraftValue] = useState(value);

    useEffect(() => {
        setDraftValue(value);
    }, [value]);

    return (
        <div className="flex gap-2">
            <Input
                value={draftValue}
                onChange={(e) => setDraftValue(e.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        onSave(draftValue);
                    }
                }}
                placeholder="Enter field ID"
                className="flex-1"
            />
            <Button onClick={() => onSave(draftValue)}>
                <Check className="h-4 w-4 mr-2" />
                Map Field
            </Button>
        </div>
    );
}
